import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import { PropsWithChildren, useCallback } from 'react'
import { Box, BoxProps, Icon, MotionBox, Paragraph } from '@ui'
import { AnimatePresence } from 'motion/react'
import { MediaFormValue } from 'routes/EditSpaceMetadata/schema/spaceMetadata.schema'

import { ImagePreview } from './ImagePreview'
import { VideoPreview } from './VideoPreview'

export const PLAYER_HEIGHT = 350

export const MediaPreview = (props: {
    selectedMedia: MediaFormValue | undefined
    fallbackImageSrc?: string
    onFileDialog?: () => void
}) => {
    const { selectedMedia, onFileDialog, fallbackImageSrc } = props

    const handleKeyDown = useCallback(
        (e: ReactKeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onFileDialog?.()
            }
        },
        [onFileDialog],
    )

    if (!selectedMedia) {
        return (
            <MediaPreviewContainer
                cursor={onFileDialog ? 'pointer' : undefined}
                role={onFileDialog ? 'button' : undefined}
                tabIndex={onFileDialog ? 0 : undefined}
                onClick={onFileDialog}
                onKeyDown={onFileDialog ? handleKeyDown : undefined}
            >
                {onFileDialog ? (
                    <Box
                        horizontal
                        paddingY="sm"
                        paddingX="x1.5"
                        gap="sm"
                        alignItems="center"
                        background="level3"
                        rounded="full"
                        color="cta2"
                    >
                        <>
                            <Icon type="image" size="square_xs" />
                            <Paragraph size="sm">Upload media</Paragraph>
                        </>
                    </Box>
                ) : fallbackImageSrc ? (
                    <Box absoluteFill>
                        <ImagePreview imageUrl={fallbackImageSrc} />
                    </Box>
                ) : null}
            </MediaPreviewContainer>
        )
    }
    const displayVariant = selectedMedia.variants?.find((variant) => variant.name === 'display')

    if (selectedMedia.type === 'video') {
        return (
            <AnimatePresence>
                <MediaPreviewContainer>
                    <FadeBox key={selectedMedia.key}>
                        <VideoPreview
                            posterUrl={displayVariant?.posterUrl}
                            hlsSource={displayVariant?.url}
                        />
                    </FadeBox>
                </MediaPreviewContainer>
            </AnimatePresence>
        )
    } else {
        return (
            <AnimatePresence>
                <MediaPreviewContainer>
                    <FadeBox key={selectedMedia.key}>
                        <ImagePreview imageUrl={displayVariant?.url} />
                    </FadeBox>
                </MediaPreviewContainer>
            </AnimatePresence>
        )
    }
}

const FadeBox: React.FC<PropsWithChildren> = ({ children }) => {
    return (
        <MotionBox
            absoluteFill
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
            {children}
        </MotionBox>
    )
}

const MediaPreviewContainer = (props: BoxProps) => {
    return (
        <Box
            grow
            centerContent
            position="relative"
            background="level2"
            rounded="md"
            overflow="hidden"
            style={{ height: '100%', aspectRatio: '0.5625' }}
            {...props}
        />
    )
}
